/**
 * Vanguard dungeon enhancements — VANGUARD_FINAL_SPEC (18 total).
 * M9-A implements a representative batch; others flagged unimplemented.
 */

export type EnhancementEffect =
  | { type: 'primary_damage_bonus_pct'; amount: number }
  | { type: 'cleave_splash_bonus_pp'; amount: number }
  | { type: 'guaranteed_bleed_bonus'; amount: number }
  | { type: 'bloodlust_heal_bonus_pp'; amount: number }
  | { type: 'twin_strike_damage_bonus_pct'; amount: number }
  | { type: 'twin_strike_crit_bonus_pp'; amount: number }
  | { type: 'unimplemented'; reason: string };

export interface VanguardEnhancementDef {
  id: string;
  parentSkillId: string;
  /** Shared by the mutually exclusive A/B pair. */
  pairId: string;
  name: string;
  description: string;
  /** Shown on offer cards — names the conflicting alternative. */
  conflictsWithName: string;
  effect: EnhancementEffect;
  /** False → eligible metadata only; cannot be selected / has no combat hook. */
  implemented: boolean;
}

/**
 * All 18 enhancements from the spec. `implemented: true` = M9-A vertical slice.
 */
export const VANGUARD_ENHANCEMENTS: readonly VanguardEnhancementDef[] = [
  // --- M9-A implemented batch (General + Twin Strike) ---
  {
    id: 'heavy_cleave',
    parentSkillId: 'cleave',
    pairId: 'cleave_enh',
    name: 'Heavy Cleave',
    description: '+10% primary attack damage on Cleave activations.',
    conflictsWithName: 'Wide Cleave',
    effect: { type: 'primary_damage_bonus_pct', amount: 10 },
    implemented: true,
  },
  {
    id: 'wide_cleave',
    parentSkillId: 'cleave',
    pairId: 'cleave_enh',
    name: 'Wide Cleave',
    description: '+10 percentage points splash damage on Cleave.',
    conflictsWithName: 'Heavy Cleave',
    effect: { type: 'cleave_splash_bonus_pp', amount: 10 },
    implemented: true,
  },
  {
    id: 'deep_wounds',
    parentSkillId: 'rend',
    pairId: 'rend_enh',
    name: 'Deep Wounds',
    description: '+1 guaranteed Bleed stack on Rend.',
    conflictsWithName: 'Savage Rend',
    effect: { type: 'guaranteed_bleed_bonus', amount: 1 },
    implemented: true,
  },
  {
    id: 'savage_rend',
    parentSkillId: 'rend',
    pairId: 'rend_enh',
    name: 'Savage Rend',
    description: '+10% attack damage on Rend activations.',
    conflictsWithName: 'Deep Wounds',
    effect: { type: 'primary_damage_bonus_pct', amount: 10 },
    implemented: true,
  },
  {
    id: 'greater_bloodlust',
    parentSkillId: 'bloodlust',
    pairId: 'bloodlust_enh',
    name: 'Greater Bloodlust',
    description: '+1 percentage point max-HP healing on Bloodlust.',
    conflictsWithName: 'Vampiric Strike',
    effect: { type: 'bloodlust_heal_bonus_pp', amount: 1 },
    implemented: true,
  },
  {
    id: 'vampiric_strike',
    parentSkillId: 'bloodlust',
    pairId: 'bloodlust_enh',
    name: 'Vampiric Strike',
    description: '+10% damage on the Bloodlust activating attack.',
    conflictsWithName: 'Greater Bloodlust',
    effect: { type: 'primary_damage_bonus_pct', amount: 10 },
    implemented: true,
  },
  {
    id: 'deadly_twin_strike',
    parentSkillId: 'twin_strike',
    pairId: 'twin_strike_enh',
    name: 'Deadly Twin Strike',
    description: '+25% damage on the added off-hand Twin Strike attack.',
    conflictsWithName: 'Precise Twin Strike',
    effect: { type: 'twin_strike_damage_bonus_pct', amount: 25 },
    implemented: true,
  },
  {
    id: 'precise_twin_strike',
    parentSkillId: 'twin_strike',
    pairId: 'twin_strike_enh',
    name: 'Precise Twin Strike',
    description: '+15 percentage points crit chance on the Twin Strike hit.',
    conflictsWithName: 'Deadly Twin Strike',
    effect: { type: 'twin_strike_crit_bonus_pp', amount: 15 },
    implemented: true,
  },

  // --- Spec-complete definitions; combat hooks deferred ---
  {
    id: 'crushing_blow',
    parentSkillId: 'shield_breaker',
    pairId: 'shield_breaker_enh',
    name: 'Crushing Blow',
    description: '+15% attack damage vs enemies below 50% HP.',
    conflictsWithName: 'Relentless Force',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred to next enhancement batch (threshold damage hook).',
    },
    implemented: false,
  },
  {
    id: 'relentless_force',
    parentSkillId: 'shield_breaker',
    pairId: 'shield_breaker_enh',
    name: 'Relentless Force',
    description: '+15% attack damage vs enemies above 50% HP.',
    conflictsWithName: 'Crushing Blow',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred to next enhancement batch (threshold damage hook).',
    },
    implemented: false,
  },
  {
    id: 'brutal_sunder',
    parentSkillId: 'sunder',
    pairId: 'sunder_enh',
    name: 'Brutal Sunder',
    description: '+25% attack damage on Sunder.',
    conflictsWithName: 'Exposing Strike',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — same primary_damage hook pattern as M9-A batch.',
    },
    implemented: false,
  },
  {
    id: 'exposing_strike',
    parentSkillId: 'sunder',
    pairId: 'sunder_enh',
    name: 'Exposing Strike',
    description: 'Apply 2 new Vulnerable stacks instead of 1.',
    conflictsWithName: 'Brutal Sunder',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — Vulnerable stack override hook.',
    },
    implemented: false,
  },
  {
    id: 'relentless_flurry',
    parentSkillId: 'flurry',
    pairId: 'flurry_enh',
    name: 'Relentless Flurry',
    description: 'Extra Flurry hits deal 70% instead of 60%.',
    conflictsWithName: 'Bloodstorm',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — Flurry damage pct override.',
    },
    implemented: false,
  },
  {
    id: 'bloodstorm',
    parentSkillId: 'flurry',
    pairId: 'flurry_enh',
    name: 'Bloodstorm',
    description: 'Each extra Flurry hit +15 pp ordinary Bleed Chance.',
    conflictsWithName: 'Relentless Flurry',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — per-hit bleed chance override on Flurry.',
    },
    implemented: false,
  },
  {
    id: 'fortified_harvest',
    parentSkillId: 'iron_harvest',
    pairId: 'iron_harvest_enh',
    name: 'Fortified Harvest',
    description: '+2 percentage points Shield generation.',
    conflictsWithName: 'Crushing Harvest',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — Iron Harvest shield pct hook.',
    },
    implemented: false,
  },
  {
    id: 'crushing_harvest',
    parentSkillId: 'iron_harvest',
    pairId: 'iron_harvest_enh',
    name: 'Crushing Harvest',
    description: '+15% damage on the Iron Harvest activating attack.',
    conflictsWithName: 'Fortified Harvest',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — primary damage bonus on Harvest activation.',
    },
    implemented: false,
  },
  {
    id: 'overcharged_cascade',
    parentSkillId: 'kinetic_cascade',
    pairId: 'kinetic_cascade_enh',
    name: 'Overcharged Cascade',
    description: '+25 percentage points accumulated-Shield damage.',
    conflictsWithName: 'Disruptive Cascade',
    effect: {
      type: 'unimplemented',
      reason: 'Deferred — Cascade shield-damage pct hook.',
    },
    implemented: false,
  },
  {
    id: 'disruptive_cascade',
    parentSkillId: 'kinetic_cascade',
    pairId: 'kinetic_cascade_enh',
    name: 'Disruptive Cascade',
    description:
      'Base 1-turn stun plus remove 1 random buff from target (stun still applies if no buffs).',
    conflictsWithName: 'Overcharged Cascade',
    effect: {
      type: 'unimplemented',
      reason:
        'Missing enemy buff catalogue / removal API — do not invent; later phase.',
    },
    implemented: false,
  },
] as const;

export const VANGUARD_ENHANCEMENT_BY_ID: Readonly<
  Record<string, VanguardEnhancementDef>
> = Object.fromEntries(VANGUARD_ENHANCEMENTS.map((e) => [e.id, e]));

export function getEnhancementDef(
  id: string,
): VanguardEnhancementDef | undefined {
  return VANGUARD_ENHANCEMENT_BY_ID[id];
}

export function enhancementsForParent(
  parentSkillId: string,
): VanguardEnhancementDef[] {
  return VANGUARD_ENHANCEMENTS.filter((e) => e.parentSkillId === parentSkillId);
}
